import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookOpen,
  faSearch,
  faFilter,
  faUser,
  faCalendarAlt,
  faCreditCard,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faChevronDown,
  faInr,
  faExclamationTriangle,
  faFileExcel,
  faChair,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { baseUrl } from "../../config/baseUrl";
import moment from "moment/moment";

export default function LibraryBookingList() {
  const [libraryData, setLibraryData] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
  const [slot, setSlot] = useState([]);
  const [slotFilter, setSlotFilter] = useState("");
  const [expiry, setExpiry] = useState("");
  const [expiryFilter, setExpiryFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [bookingsPerPage] = useState(10);

  // Seat map state
  const [seatMapModal, setSeatMapModal] = useState(false);
  const [seatMapData, setSeatMapData] = useState([]);
  const [seatMapLoading, setSeatMapLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const userDetails = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    if (userDetails?._id) {
      fetchBookings();
    }
  }, [
    currentPage,
    searchTerm,
    statusFilter,
    paymentMethodFilter,
    startDate,
    endDate,
    slotFilter,
    expiryFilter,
  ]);

  const buildApiUrl = () => {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (paymentMethodFilter) params.append("paymentMethod", paymentMethodFilter);
    if (statusFilter) params.append("status", statusFilter);
    if (currentPage) params.append("page", currentPage.toString());
    if (slotFilter) params.append("slot", slotFilter);
    if (expiryFilter) params.append("expiry", expiryFilter);

    const queryString = params.toString();
    return `${baseUrl}/owner/booking/${userDetails?._id}${queryString ? `?${queryString}` : ""}`;
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const apiUrl = buildApiUrl();

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      });

      const data = await response.json();
      if (data.status) {
        setLibraryData(data?.data);

        let fetchedBookings = data.data.bookings || [];

        // Apply client-side search filter for user name/email
        if (searchTerm) {
          fetchedBookings = fetchedBookings.filter(
            (booking) =>
              booking.userId?.name
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              booking.userId?.email
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase())
          );
        }

        setBookings(fetchedBookings);
        setSlot(data.data?.library?.slots || []);
        setTotalBookings(data.data.totalCount || 0);
        setTotalPages(data.data.totalPages || 1);
      } else {
        toast.error(data.message || "Failed to fetch bookings");
        setBookings([]);
      }
    } catch (error) {
      toast.error("Failed to fetch bookings");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to change status to ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, change to ${newStatus}!`,
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(
          `${baseUrl}/library-booking/update-status/${bookingId}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: localStorage.getItem("token"),
            },
            body: JSON.stringify({ status: newStatus }),
          }
        );

        const data = await response.json();
        if (data.status) {
          setBookings((prevBookings) =>
            prevBookings.map((booking) =>
              booking._id === bookingId
                ? { ...booking, status: newStatus }
                : booking
            )
          );
          toast.success(`Status updated to ${newStatus} successfully`);
        } else {
          toast.error(data.message || "Failed to update status");
        }
      } catch (error) {
        toast.error("Failed to update status");
      }
    }
  };

  const getStatusBadge = (status, bookingId) => {
    const statusConfig = {
      pending: { color: "warning", icon: faClock, text: "Pending" },
      confirmed: { color: "success", icon: faCheckCircle, text: "Confirmed" },
      cancelled: { color: "danger", icon: faTimesCircle, text: "Cancelled" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    const availableStatuses = [
      { value: "pending", label: "Pending", icon: faClock },
      { value: "confirmed", label: "Confirmed", icon: faCheckCircle },
      { value: "cancelled", label: "Cancelled", icon: faTimesCircle },
    ].filter((s) => s.value !== status);

    return (
      <div className="dropdown">
        <span
          className={`status-badge status-${config.color} dropdown-toggle`}
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <FontAwesomeIcon icon={config.icon} size="sm" />
          <span className="status-text">{config.text}</span>
          <FontAwesomeIcon icon={faChevronDown} size="xs" />
        </span>
        <ul className="dropdown-menu dropdown-menu-end">
          {availableStatuses.map((statusOption) => (
            <li key={statusOption.value}>
              <button
                className={`dropdown-item ${statusOption.value === "cancelled" ? "text-danger" : ""}`}
                onClick={() => handleStatusChange(bookingId, statusOption.value)}
              >
                <FontAwesomeIcon icon={statusOption.icon} className="me-2" />
                {statusOption.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const getPaymentMethodBadge = (method) => {
    return (
      <span
        className={`payment-badge ${method === "online" ? "payment-online" : "payment-offline"}`}
      >
        <FontAwesomeIcon icon={faCreditCard} size="sm" />
        <span className="payment-text">
          {method === "online" ? "Online" : "Offline"}
        </span>
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setPaymentMethodFilter("");
    setStartDate("");
    setEndDate("");
    setSlotFilter("");
    setCurrentPage(1);
    setExpiryFilter("");
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getExpiringInText = (endDate) => {
    const now = moment();
    const end = moment(endDate);

    if (end.isBefore(now)) {
      return { text: "Expired", type: "expired", days: 0 };
    }

    const diffDays = end.diff(now, "days");

    if (diffDays <= 30) {
      return {
        text: `${diffDays} day${diffDays !== 1 ? "s" : ""} `,
        type: diffDays <= 7 ? "critical" : "warning",
        days: diffDays,
      };
    }

    return {
      text: end.fromNow(true) + " left",
      type: "normal",
      days: diffDays,
    };
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (statusFilter) count++;
    if (paymentMethodFilter) count++;
    if (startDate) count++;
    if (endDate) count++;
    if (slotFilter) count++;
    if (expiryFilter) count++;
    return count;
  };

  const fetchSeatMap = async (booking) => {
    if (!booking?.slotId) {
      toast.error("Slot information not available for this booking");
      return;
    }
    setSelectedBooking(booking);
    setSeatMapModal(true);
    setSeatMapLoading(true);
    setSeatMapData([]);
    try {
      const params = new URLSearchParams({
        slotId: booking.slotId,
        bookingId: booking._id,
        startDate: moment(booking.startDate).format("YYYY-MM-DD"),
        endDate: moment(booking.endDate).format("YYYY-MM-DD"),
      });
      const response = await fetch(
        `${baseUrl}/library-booking/seat-map?${params.toString()}`,
        {
          method: "GET",
          headers: { Authorization: localStorage.getItem("token") },
        }
      );
      const data = await response.json();
      if (data.status) {
        setSeatMapData(data.data || []);
      } else {
        toast.error(data.message || "Failed to load seat map");
        setSeatMapModal(false);
      }
    } catch (error) {
      toast.error("Failed to load seat map");
      setSeatMapModal(false);
    } finally {
      setSeatMapLoading(false);
    }
  };

  const buildQueryString = () => {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (paymentMethodFilter) params.append("paymentMethod", paymentMethodFilter);
    if (statusFilter) params.append("status", statusFilter);
    if (currentPage) params.append("page", currentPage.toString());
    if (slotFilter) params.append("slot", slotFilter);
    if (expiryFilter) params.append("expiry", expiryFilter);

    return params.toString() ? `?${params.toString()}` : "";
  };

  const handleExportExcel = () => {
    const queryString = buildQueryString();
    const url = `${baseUrl}/owner/reports/booking-report-download/${userDetails?._id}${queryString}`;

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "BookingReport.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <section className="main-sec">
        <div className="container-fluid">
          {/* Header */}
          <div className="row mb-4">
            <div className="col-lg-8">
              <div className="dashboard-title">
                <h4 className="dash-head">
                  <FontAwesomeIcon icon={faBookOpen} className="me-2" />
                  {userDetails?.libraryName}
                </h4>
              </div>
              <nav aria-label="breadcrumb" className="custom-bredcump">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Library Bookings
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          {/* Filters Card */}
          <div className="card mb-4 shadow-sm">
            <div className="card-body">
              <div className="row g-3">
                {/* Date Range */}
                <div className="col-6 col-md-3 col-lg-2">
                  <label className="form-label small text-muted">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                  />
                </div>

                <div className="col-6 col-md-3 col-lg-2">
                  <label className="form-label small text-muted">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                  />
                </div>

                {/* Status Filter */}
                <div className="col-6 col-md-3 col-lg-2">
                  <label className="form-label small text-muted">Status</label>
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Payment Method */}
                <div className="col-6 col-md-3 col-lg-2">
                  <label className="form-label small text-muted">Payment</label>
                  <select
                    className="form-select"
                    value={paymentMethodFilter}
                    onChange={(e) => { setPaymentMethodFilter(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="">All</option>
                    <option value="online">Online</option>
                    <option value="offline">Offline</option>
                  </select>
                </div>

                {/* Slot Filter */}
                <div className="col-6 col-md-3 col-lg-2">
                  <label className="form-label small text-muted">Slot</label>
                  <select
                    className="form-select"
                    value={slotFilter}
                    onChange={(e) => { setSlotFilter(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="">All Slots</option>
                    {slot.map((s, idx) => (
                      <option key={idx} value={s._id}>
                        {s.slotId}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Expiry Filter */}
                <div className="col-6 col-md-3 col-lg-2">
                  <label className="form-label small text-muted">Expiring In</label>
                  <select
                    className="form-select"
                    value={expiryFilter}
                    onChange={(e) => { setExpiryFilter(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="">All</option>
                    {[1, 2, 3, 4, 5, 7, 10, 15, 30].map((d) => (
                      <option key={d} value={d}>
                        {d} day{d !== 1 ? "s" : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Clear Button */}
                <div className="col-12 col-md-3 col-lg-1 d-flex align-items-end w-100">
                  <button
                    className="btn btn-outline-secondary w-100 clear_filters-btn d-flex justify-content-center align-items-center"
                    onClick={handleClearFilters}
                    disabled={getActiveFiltersCount() === 0}
                  >
                    <FontAwesomeIcon icon={faFilter} className="me-1" />
                    Clear
                  </button>
                </div>
              </div>

              {/* Active Filters */}
              {getActiveFiltersCount() > 0 && (
                <div className="active-filters mt-3">
                  <small className="text-muted me-2">
                    Active filters ({getActiveFiltersCount()}):
                  </small>
                  {searchTerm && <span className="filter-tag">Search: {searchTerm}</span>}
                  {statusFilter && <span className="filter-tag">Status: {statusFilter}</span>}
                  {paymentMethodFilter && <span className="filter-tag">Payment: {paymentMethodFilter}</span>}
                  {startDate && <span className="filter-tag">From: {moment(startDate).format("MMM DD")}</span>}
                  {endDate && <span className="filter-tag">To: {moment(endDate).format("MMM DD")}</span>}
                  {slotFilter && <span className="filter-tag">Slot: {slotFilter}</span>}
                  {expiryFilter && <span className="filter-tag">Expiry: {expiryFilter} days</span>}
                </div>
              )}
            </div>
          </div>

          {/* Results Summary */}
          <div className="row mb-3">
            <div className="col-lg-6">
              <h5 className="mb-0">
                Bookings List ({bookings.length} of {totalBookings})
              </h5>
              <small className="text-muted">(Click Student Name for Booking Details)</small>
            </div>
            <div className="col-lg-6 mb-4 text-end">
              <div className="export-buttons">
                <button className="btn btn-success btn-sm me-2" onClick={handleExportExcel}>
                  <FontAwesomeIcon icon={faFileExcel} className="me-1" />
                  Export Excel
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card shadow-sm">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "60px" }}>SN</th>
                    <th style={{ width: "200px" }}>User Details</th>
                    <th style={{ width: "180px" }}>Period</th>
                    <th style={{ width: "150px" }}>Booking Date</th>
                    <th style={{ width: "120px" }}>Expiring In</th>
                    <th style={{ width: "120px" }}>Payment</th>
                    <th style={{ width: "120px" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5">
                        <div className="d-flex flex-column align-items-center">
                          <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mb-0 fw-medium">Loading bookings...</p>
                        </div>
                      </td>
                    </tr>
                  ) : bookings.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5">
                        <FontAwesomeIcon icon={faBookOpen} size="3x" className="text-muted mb-3" />
                        <h4>No Bookings Found!</h4>
                        <p className="text-muted">
                          {getActiveFiltersCount() > 0
                            ? "Try adjusting your search or filter criteria"
                            : "No library bookings available at the moment"}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    bookings.map((booking, i) => {
                      const expiryInfo = getExpiringInText(booking.endDate);
                      return (
                        <tr key={booking._id}>
                          <td className="fw-medium">
                            {(currentPage - 1) * bookingsPerPage + i + 1}
                          </td>
                          <td>
                            <div className="user-info">
                              <div className="user-name">
                                <Link to={`/student/${booking._id}`} className="text-decoration-none">
                                  <FontAwesomeIcon icon={faUser} className="me-1 text-primary" />
                                  {booking.userId?.name || "N/A"}
                                </Link>
                              </div>
                              <div className="user-email">
                                ID: {booking.userId?.userCode || "N/A"}
                              </div>
                              <div className="booking-id">
                                Ref: {booking.bookingId || "N/A"}
                              </div>
                              <button
                                className="btn btn-sm seat-map-btn mt-1"
                                onClick={() => fetchSeatMap(booking)}
                              >
                                <FontAwesomeIcon icon={faChair} className="me-1" />
                                Seat Map
                              </button>
                            </div>
                          </td>
                          <td>
                            <div className="period-info">
                              <div className="period-dates">
                                <div className="start-date">
                                  {moment(booking.startDate).format("MMM DD, YYYY")}
                                </div>
                                <span className="text-muted">-</span>
                                <div className="end-date">
                                  {moment(booking.endDate).format("MMM DD, YYYY")}
                                </div>
                                <div className="booking-time">
                                  {booking?.fromTime}-{booking?.toTime}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="booking-date">
                              <div style={{ fontSize: "14px" }}>
                                {moment(booking.createdAt).format("MMM DD, YYYY")}
                              </div>
                              <div className="booking-time">
                                {moment(booking.createdAt).format("hh:mm A")}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className={`expiry-info expiry-${expiryInfo.type}`}>
                              {expiryInfo.type === "expired" && (
                                <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                              )}
                              {expiryInfo.type === "critical" && (
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                              )}
                              <span className="expiry-text text-danger">{expiryInfo.text}</span>
                            </div>
                          </td>
                          <td>
                            <div className="payment-info text-center">
                              {getPaymentMethodBadge(booking.paymentMethod)}
                              {booking.amount > 0 && (
                                <div className="payment-amount text-center">
                                  ₹{booking.amount}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{getStatusBadge(booking.status, booking._id)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container mt-4">
              <nav aria-label="Bookings pagination">
                <ul className="pagination justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <li
                          key={pageNumber}
                          className={`page-item ${currentPage === pageNumber ? "active" : ""}`}
                        >
                          <button className="page-link" onClick={() => paginate(pageNumber)}>
                            {pageNumber}
                          </button>
                        </li>
                      );
                    } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                      return (
                        <li key={pageNumber} className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      );
                    }
                    return null;
                  })}
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
              <div className="text-center mt-2">
                <small className="text-muted">
                  Showing page {currentPage} of {totalPages} ({bookings.length} of {totalBookings} bookings)
                </small>
              </div>
            </div>
          )}
        </div>
      </section>

      <style>
        {`
          .search-icon {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            color: #6c757d;
            z-index: 2;
          }

          .active-filters {
            padding: 0.75rem;
            background-color: #f8f9fa;
            border-radius: 6px;
            border: 1px solid #e9ecef;
          }

          .filter-tag {
            display: inline-block;
            background: #e3f2fd;
            color: #1976d2;
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-size: 0.8rem;
            margin-right: 0.5rem;
            margin-bottom: 0.25rem;
          }

          .table {
            font-size: 0.9rem;
          }

          .table th {
            font-weight: 600;
            color: #495057;
            border-bottom: 2px solid #dee2e6;
            padding: 1rem 0.75rem;
          }

          .table td {
            padding: 1rem 0.75rem;
            vertical-align: middle;
          }

          .table tbody tr:hover {
            background-color: #f8f9fa;
          }

          .user-info {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }

          .user-name {
            font-weight: 600;
            color: #495057;
          }

          .user-name a { color: #0d6efd; }
          .user-name a:hover { color: #0a58ca; }

          .user-email {
            font-size: 0.8rem;
            color: #6c757d;
          }

          .booking-id {
            font-size: 0.75rem;
            color: #6c757d;
            font-family: monospace;
          }

          .period-info {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }

          .period-dates {
            display: flex;
            flex-direction: column;
            gap: 0.125rem;
          }

          .start-date, .end-date {
            font-weight: 500;
            font-size: 0.85rem;
          }

          .time-slot {
            background: #e3f2fd;
            color: #1976d2;
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-size: 0.75rem;
            text-align: center;
            font-weight: 500;
          }

          .booking-date {
            display: flex;
            flex-direction: column;
            gap: 0.125rem;
            align-items: flex-start;
          }

          .booking-time {
            font-size: 0.75rem;
            color: #6c757d;
          }

          .expiry-info {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            font-weight: 500;
            font-size: 0.85rem;
          }

          .expiry-expired  { color: #dc3545; }
          .expiry-critical { color: #fd7e14; }
          .expiry-warning  { color: #ffc107; }
          .expiry-normal   { color: #28a745; }

          .payment-info {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }

          .payment-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 500;
          }

          .payment-online  { background: #e3f2fd; color: #1976d2; }
          .payment-offline { background: #f3e5f5; color: #7b1fa2; }

          .payment-amount {
            font-size: 0.8rem;
            font-weight: 600;
            color: #28a745;
          }

          .status-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
            padding: 0.375rem 0.75rem;
            border-radius: 6px;
            font-size: 0.8rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
            text-decoration: none;
          }

          .status-badge:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }

          .status-warning { background: #fff3cd; color: #856404; border: 1px solid #ffeaa7; }
          .status-success { background: #d1edff; color: #0f5132; border: 1px solid #a3cfbb; }
          .status-danger  { background: #f8d7da; color: #721c24; border: 1px solid #f1aeb5; }

          .status-text { font-weight: 500; }

          .dropdown-menu {
            border: 1px solid #dee2e6;
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
            border-radius: 6px;
          }

          .dropdown-item {
            padding: 0.5rem 1rem;
            font-size: 0.85rem;
          }

          .dropdown-item:hover { background-color: #f8f9fa; }

          @media (max-width: 768px) {
            .table { font-size: 0.8rem; }
            .table th, .table td { padding: 0.5rem 0.25rem; }
            .status-badge { padding: 0.25rem 0.5rem; font-size: 0.7rem; }
            .payment-badge { font-size: 0.7rem; padding: 0.2rem 0.4rem; }
          }

          .spinner-border { width: 3rem; height: 3rem; }

          .pagination-container {
            border-top: 1px solid #dee2e6;
            padding-top: 1rem;
          }

          .page-link { color: #0d6efd; border-color: #dee2e6; }
          .page-item.active .page-link { background-color: #0d6efd; border-color: #0d6efd; }

          .clear_filters-btn { white-space: nowrap; }

          /* Seat Map Button */
          .seat-map-btn {
            background: #e8f5e9;
            color: #2e7d32;
            border: 1px solid #a5d6a7;
            font-size: 0.72rem;
            padding: 0.15rem 0.5rem;
            border-radius: 4px;
          }
          .seat-map-btn:hover {
            background: #26a084;
            color: #fff;
            border-color: #26a084;
          }

          /* Seat Map Modal */
          .seat-map-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.55);
            z-index: 1050;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1rem;
          }

          .seat-map-dialog {
            background: #fff;
            border-radius: 12px;
            width: 100%;
            max-width: 640px;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          }

          .seat-map-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 1rem 1.25rem;
            border-bottom: 1px solid #e9ecef;
            background: #26a084;
            border-radius: 12px 12px 0 0;
          }

          .seat-map-header h5 {
            margin: 0;
            color: #fff;
            font-weight: 600;
          }

          .seat-map-close {
            background: none;
            border: none;
            color: #fff;
            font-size: 1.25rem;
            cursor: pointer;
            line-height: 1;
            padding: 0.25rem;
          }

          .seat-map-body { padding: 1.25rem; }

          .seat-map-legend {
            display: flex;
            gap: 1.25rem;
            margin-bottom: 1.25rem;
            flex-wrap: wrap;
          }

          .legend-item {
            display: flex;
            align-items: center;
            gap: 0.4rem;
            font-size: 0.82rem;
            font-weight: 500;
            color: #495057;
          }

          .legend-dot {
            width: 14px;
            height: 14px;
            border-radius: 3px;
            flex-shrink: 0;
          }

          .legend-dot.available { background: #26a084; }
          .legend-dot.occupied  { background: #dc3545; }
          .legend-dot.current   { background: #0d6efd; }

          .seat-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
            gap: 0.65rem;
          }

          .seat-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            border-radius: 8px;
            padding: 0.65rem 0.4rem;
            font-size: 0.78rem;
            font-weight: 600;
            border: 2px solid transparent;
            transition: transform 0.15s;
            cursor: default;
          }

          .seat-card:hover { transform: translateY(-2px); }

          .seat-card.available { background: #e8f5e9; border-color: #26a084; color: #1b5e20; }
          .seat-card.occupied  { background: #fdecea; border-color: #dc3545; color: #b71c1c; }
          .seat-card.current   { background: #e3f0ff; border-color: #0d6efd; color: #0a3880; }

          .seat-icon { font-size: 1.3rem; margin-bottom: 0.2rem; }
          .seat-number { font-size: 0.8rem; }

          .seat-time {
            font-size: 0.65rem;
            font-weight: 400;
            text-align: center;
            margin-top: 0.15rem;
            opacity: 0.85;
            word-break: break-word;
          }

          .seat-map-info {
            background: #f8f9fa;
            border-radius: 8px;
            padding: 0.75rem 1rem;
            margin-bottom: 1rem;
            font-size: 0.83rem;
            color: #495057;
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem 2rem;
          }

          .seat-map-info span strong { color: #26a084; }
        `}
      </style>

      {/* Seat Map Modal */}
      {seatMapModal && (
        <div className="seat-map-overlay" onClick={() => setSeatMapModal(false)}>
          <div className="seat-map-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="seat-map-header">
              <h5>
                <FontAwesomeIcon icon={faChair} className="me-2" />
                Seat Availability Map
              </h5>
              <button className="seat-map-close" onClick={() => setSeatMapModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="seat-map-body">
              {selectedBooking && (
                <div className="seat-map-info">
                  <span><strong>Student:</strong> {selectedBooking.userId?.name || "N/A"}</span>
                  <span><strong>Slot:</strong> {selectedBooking.fromTime} – {selectedBooking.toTime}</span>
                  <span>
                    <strong>Period:</strong>{" "}
                    {moment(selectedBooking.startDate).format("MMM DD")} –{" "}
                    {moment(selectedBooking.endDate).format("MMM DD, YYYY")}
                  </span>
                </div>
              )}

              <div className="seat-map-legend">
                <div className="legend-item"><div className="legend-dot available" /> Available</div>
                <div className="legend-item"><div className="legend-dot occupied" /> Occupied</div>
                <div className="legend-item"><div className="legend-dot current" /> This Booking</div>
              </div>

              {seatMapLoading ? (
                <div className="text-center py-4">
                  <div
                    className="spinner-border text-success"
                    role="status"
                    style={{ width: "2.5rem", height: "2.5rem" }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 mb-0 text-muted small">Loading seat map...</p>
                </div>
              ) : seatMapData.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <FontAwesomeIcon icon={faChair} size="2x" className="mb-2" />
                  <p className="mb-0">No seat data available.</p>
                </div>
              ) : (
                <>
                  <div className="seat-grid">
                    {seatMapData.map((seat) => {
                      // Highlight the seat that belongs to this booking
                      const isCurrentBooking =
                        selectedBooking?.seatNo != null &&
                        Number(seat.seatNo) === Number(selectedBooking.seatNo);

                      const cardClass = isCurrentBooking
                        ? "current"
                        : seat.status === "occupied"
                          ? "occupied"
                          : "available";

                      return (
                        <div key={seat.seatNo} className={`seat-card ${cardClass}`}>
                          <div className="seat-icon">
                            <FontAwesomeIcon icon={faChair} />
                          </div>
                          <div className="seat-number">Seat {seat.seatNo}</div>
                          {seat.bookedTime && seat.bookedTime.length > 0 && (
                            <div className="seat-time">{seat.bookedTime.join(", ")}</div>
                          )}
                          <div className="seat-time" style={{ textTransform: "capitalize" }}>
                            {isCurrentBooking ? "This Booking" : seat.status}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="text-end mt-3">
                    <small className="text-muted">
                      {seatMapData.filter((s) => s.status === "available").length} available &middot;{" "}
                      {seatMapData.filter((s) => s.status === "occupied").length} occupied
                    </small>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
